/**
 * Product options REST client with short in-memory cache.
 */
import { getSettings, getConfig } from '../config.js';
import { cacheKey, activeCurrency, activeLanguage } from '../utils/currency.js';
import { emit } from '../events/emitter.js';

const cache = new Map();

function ttlMs() {
	const ttl = Number(getConfig().optionsCacheTtl);
	return (Number.isFinite(ttl) ? ttl : 30) * 1000;
}

export function clearOptionsCache(productId) {
	if (productId) {
		const key = cacheKey(productId);
		cache.delete(key);
		return;
	}
	cache.clear();
}

export async function fetchProductOptions(productId, context = 'archive', signal) {
	const key = cacheKey(productId);
	const cached = cache.get(key);
	const now = Date.now();

	if (cached && cached.expires > now) {
		return cached.data;
	}

	const settings = getSettings();
	const currency = activeCurrency();
	const language = activeLanguage();
	let url = `${settings.restUrl}/products/${productId}/options?context=${encodeURIComponent(context)}&currency=${encodeURIComponent(currency)}`;
	if (language) {
		url += `&bc_lang=${encodeURIComponent(language)}`;
	}

	emit('bcQuickBuy:optionsRequested', { productId, context });

	const response = await fetch(url, {
		method: 'GET',
		credentials: 'same-origin',
		headers: {
			Accept: 'application/json',
			'X-WP-Nonce': settings.wpRestNonce || '',
		},
		signal,
	});

	if (!response.ok) {
		let payload = {};
		try {
			payload = await response.json();
		} catch (e) {
			payload = {};
		}
		const error = new Error(payload.message || 'Options request failed');
		error.status = response.status;
		error.code = payload.code || 'options_failed';
		emit('bcQuickBuy:optionsFailed', {
			productId,
			context,
			errorCode: error.code,
		});
		throw error;
	}

	const data = await response.json();
	cache.set(key, { data, expires: now + ttlMs() });
	emit('bcQuickBuy:optionsLoaded', {
		productId,
		context,
		productName: data.title || '',
	});
	return data;
}
